import { useLocalization, toast, useGoBack, createTriggerResource, DeleteStatus } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { ViolationsRepository } from '~/repositories/violationsRepository';
import type { HydrateFile } from '@magicdoorinc/ui';
import type {
  AddAssociationViolationLeaseBillDto,
  AssociationViolationStatus,
  CreateAssociationViolationRequestDto,
  CreateWorkOrderFromAssociationViolationDto,
  HydratedWorkOrderDto,
  SendAssociationViolationNoticeDto,
  UpdateAssociationViolationRequestDto,
} from '~/swagger/portal/Api';

const repo = new ViolationsRepository();
export const [ViolationsProvider, useViolations] = createMagicDoorContext('Violations', () => {
  const [filtered, setFilter, actions] = createTriggerResource((filter: any) => repo.list(filter));
  const { t } = useLocalization();
  const goBack = useGoBack();

  const [createdWorkOrder, setCreatedWorkOrder] = createSignal<Partial<HydratedWorkOrderDto>>({});

  const [filesList, setFilesList] = createSignal<HydrateFile[]>([]);

  const addViolation = async (payload: CreateAssociationViolationRequestDto) => {
    const response = await repo.add(payload);

    const filesToUpload = filesList().filter((f) => f.rawFile);
    if (response.id && filesToUpload.length > 0) {
      await repo.uploadViolationFiles(
        response.id,
        filesToUpload.map((f) => f.rawFile!)
      );
    }

    setFilesList([]);
    return response;
  };

  const [violation, getViolation, violationActions] = createTriggerResource(repo.get.bind(repo));

  const deleteViolation = async (id?: string) => {
    if (!id) return;
    try {
      await repo.delete(id);
      actions.refetch();
      toast(t('Delete success'), 'success');
    } catch {
      toast(t('Delete failed, please try again later'), 'error');
    }
  };

  const handleStatusChange = async (id: string | undefined, status: AssociationViolationStatus) => {
    if (!id) return;
    try {
      await repo.changeStatus(id, status);
      actions.refetch();
      toast(t('Modify status success'), 'success');
    } catch {
      toast(t('Modify status failed, please try again later'), 'error');
    }
  };

  const updateViolation = async (id: string, payload: UpdateAssociationViolationRequestDto) => {
    try {
      const response = await repo.update(id, payload);

      const filesToDelete = filesList().filter((f) => f.deleted === DeleteStatus.DELETED);
      const filesToUpload = filesList().filter((f) => f.rawFile);

      for (const file of filesToDelete) {
        await repo.deleteViolationFile(id, file.idOrUrl);
      }

      if (filesToUpload.length > 0) {
        await repo.uploadViolationFiles(
          id,
          filesToUpload.map((f) => f.rawFile!)
        );
      }

      setFilesList([]);
      violationActions.refetch();
      actions.refetch();
      toast(t('Update success'), 'success');
      goBack();
      return response;
    } catch {
      toast(t('Update failed, please try again later'), 'error');
    }
  };

  const createFineBill = async (data: { dueDate: string }) => {
    try {
      const id = violation()?.id;
      if (!id) return;
      await repo.createFineBill(id, data);
      violationActions.refetch();
      toast(t('Create fine bill success'), 'success');
    } catch {
      toast(t('Create fine bill failed, please try again later'), 'error');
    }
  };

  const createWorkOrder = async (data: CreateWorkOrderFromAssociationViolationDto) => {
    try {
      const id = violation()?.id;
      if (!id) return;
      const res = await repo.createWorkOrder(id, data);
      setCreatedWorkOrder(res);
      violationActions.refetch();
      toast(t('Create fine bill success'), 'success');
    } catch {
      toast(t('Create fine bill failed, please try again later'), 'error');
    }
  };

  const createLeaseBill = async (data: AddAssociationViolationLeaseBillDto) => {
    const id = violation()?.id;
    if (!id) return;
    await repo.createLeaseBill(id, data);
    violationActions.refetch();
    toast(t('Create lease bill success'), 'success');
  };

  const createOwnerNotification = async (payload: SendAssociationViolationNoticeDto) => {
    const id = violation()?.id;
    if (!id) return;
    await repo.createOwnerNotification(id, payload);
    violationActions.refetch();
  };

  const createLeaseNotification = async (payload: SendAssociationViolationNoticeDto) => {
    const id = violation()?.id;
    if (!id) return;
    await repo.createLeaseNotification(id, payload);
    violationActions.refetch();
  };

  return {
    filtered: filtered,
    setFilter: (filter: any) => setFilter(filter),
    refetch: actions.refetch,
    addViolation,
    getViolation,
    violation,
    violationActions,
    deleteViolation,
    handleStatusChange,
    updateViolation: updateViolation,
    createFineBill,
    createWorkOrder,
    createdWorkOrder,
    createLeaseBill,
    createOwnerNotification,
    createLeaseNotification,
    filesList,
    setFilesList,
  };
});
